import { useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import ProtectedRoutes from "./utils/ProtectedRoutes";
import SignIn from "./pages/SignIn";
import Register from "./pages/RegisterForm";
import Dashboard from "./pages/Dashboard";
import Dashboard2 from "./pages/Dashboard2";
import Regions from "./pages/Regions";
import Places from "./pages/Places";
import Devices from "./pages/Devices";
import Users from "./pages/Users";
import Reports from "./pages/Reports";
import Stocks from "./pages/Stock";
import NodeDetail from "./pages/NodeDetail";
import Telemetry from "./pages/Telemetry";
import { RefreshIntervalProvider } from "./contexts/RefreshIntervalContext";
import { SidebarProvider } from "./contexts/SidebarContext";
import { getCurrentTenant } from "./config/tenantConfig";
import AdminConfigurations from "./pages/AdminConfigurations";
import CreateRoute from "./pages/CreateRoute";
import VendingEvents from "./pages/VendingEvents";
import ProjectList from "./pages/ProjectList";
import ErrorBoundary from "./components/ErrorBoundary";
import ErrorTestButton from "./components/ErrorTestButton";
import VersionUpdatePopup from "./components/VersionUpdatePopup";
import ConfirmRole from "./pages/ConfirmRole";
import ClientSelect from "./pages/ClientSelect";
import DigitalWallet from "./pages/DigitalWallet";

function App() {
  // Get the tenant configuration based on the current hostname
  const tenantConfig = getCurrentTenant();
  const { primary } = tenantConfig.theme;

  // Set document title based on tenant
  useEffect(() => {
    document.title = tenantConfig.title || tenantConfig.name;

    // Update favicon if tenant has one
    if (tenantConfig.favicon) {
      const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
      link.type = 'image/x-icon';
      link.rel = 'shortcut icon';
      link.href = tenantConfig.favicon;
      document.getElementsByTagName('head')[0].appendChild(link);
    }
  }, [tenantConfig.title, tenantConfig.name, tenantConfig.favicon]);

  // Create MUI theme based on tenant's primary color
  const muiTheme = createTheme({
    palette: {
      primary: { main: primary },
    },
  });

  return (
    <ThemeProvider theme={muiTheme}>
      <ErrorBoundary>
        <VersionUpdatePopup />
        <Router>
          <SidebarProvider>
          <RefreshIntervalProvider>
            <Routes>
              {/* Root redirects based on authToken and companyName in localStorage */}
              <Route
                path="/"
                element={
                  (() => {
                    const authToken = localStorage.getItem("authToken");
                    const companyName = localStorage.getItem("companyName");

                    if (!authToken) {
                      return <Navigate to="/login" />;
                    }

                    // If user has DefaultClient or no client selected, go to client selection
                    if (!companyName || companyName === "DefaultClient") {
                      return <Navigate to="/client" />;
                    }

                    // Check if companyName has corresponding data in localStorage
                    const companyData = localStorage.getItem(companyName);
                    if (!companyData) {
                      return <Navigate to="/client" />;
                    }

                    // User has a valid client with data, go to dashboard
                    return <Navigate to="/dashboard" />;
                  })()
                }
              />

              {/* Pass tenant information to the SignIn component */}
              <Route
                path="/login"
                element={<SignIn tenantConfig={tenantConfig} />}
              />

              {/* Register route - only for default tenant */}
              {tenantConfig.name === "LettaIoT" && (
                <Route
                  path="/register"
                  element={<Register tenantConfig={tenantConfig} />}
                />
              )}

              {/* Confirm Role - login required but no sidebar */}
              <Route path="/confirmRole" element={<ConfirmRole />} />

              {/* Client Select - for choosing or creating a client after confirmRole */}
              <Route path="/client" element={<ClientSelect />} />

              {/* Protected routes */}
              <Route element={<ProtectedRoutes />}>
                <Route path="/dashboard" element={<Dashboard />} />
                {/* Gizli route: menüde/tab'da yok, sadece URL ile erişilir */}
                <Route path="/dashboard2" element={<Dashboard2 />} />
                <Route path="/regions" element={<Regions />} />
                {/* RCU: /places shows the live node list (NodeDetail); other
                    tenants keep the places management grid (Places). */}
                <Route
                  path="/places"
                  element={
                    tenantConfig.projectCode === "RCU" ? (
                      <NodeDetail />
                    ) : (
                      <Places />
                    )
                  }
                />
                <Route path="/places/:idNode" element={<Telemetry />} />
                {/* RCU: places management grid lives here. */}
                <Route path="/place-definitions" element={<Places />} />
                <Route path="/vending-definitions" element={<Places />} />
                <Route path="/vending-events" element={<VendingEvents />} />
                <Route path="/vending-machines/:idNode" element={<Telemetry />} />
                <Route path="/vending-machines" element={<NodeDetail />} />
                <Route path="/node-detail" element={<NodeDetail />} />
                <Route path="/stock-management" element={<Stocks />} />
                <Route path="/devices" element={<Devices />} />
                <Route
                  path="/admin-configurations"
                  element={<AdminConfigurations />}
                />
                <Route path="/users" element={<Users />} />
                <Route path="/digital-wallet" element={<DigitalWallet />} />
                <Route path="/create-route" element={<CreateRoute />} />
              </Route>
              <Route path="/projects" element={<ProjectList />} />
              <Route path="/reports" element={<Reports />} />
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
            {/* Error Boundary test butonu - sadece development */}
            {process.env.NODE_ENV === "development" && <ErrorTestButton />}
          </RefreshIntervalProvider>
          </SidebarProvider>
        </Router>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;
